import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Settings from '@Images/account_popup_settings.svg'
import Users from '@Images/account_popup_users.svg'
import Billing from '@Images/account_popup_billing.svg'
import Plan from '@Images/account_popup_plan.svg'
import Statistics from '@Images/account_popup_statistics.svg'
import Docs from '@Images/account_popup_docs.svg'
import Arrow from '@Images/account_popup_arrow.svg'
import Logout from '@Images/account_popup_logout.svg'
import Documentation from '@Images/account_popup_documentation.svg'
import Changelog from '@Images/account_popup_changelog.svg'
import Api from '@Images/account_popup_api.svg'
import ExternalLink from '@Images/account_popup_link.svg'
import Theme from '@Images/account_popup_theme.svg'
import DarkTheme from '@Images/account_popup_dark_theme.svg'
import LightTheme from '@Images/account_popup_light_theme.svg'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'

interface MenuProps {
  readonly userIsAccountAdmin: boolean
  readonly currentAccountHasEnterpriseOrCustomSubscription: boolean
  readonly currentAccountHasCurrentSubscription: boolean
  logout(): void
  createCustomerPortalSession(): void
  closeAccountPopup(): void
  handleSetUseLightTheme(): void
  handleResetUseLightTheme(): void
}

const Menu: React.FC<MenuProps> = props => {
  const {
    userIsAccountAdmin,
    currentAccountHasEnterpriseOrCustomSubscription,
    currentAccountHasCurrentSubscription,
  } = props

  const [showDocsSubMenu, setShowDocsSubMenu] = useState(false)
  const [showThemeSubMenu, setShowThemeSubMenu] = useState(false)

  useClickOutsideListener('AccountPopup__docs-submenu-element', setShowDocsSubMenu, showDocsSubMenu)
  useClickOutsideListener('AccountPopup__theme-submenu-element', setShowThemeSubMenu, showThemeSubMenu)

  const shouldRenderBillingAndPlan = userIsAccountAdmin && (!currentAccountHasEnterpriseOrCustomSubscription || !currentAccountHasCurrentSubscription)
  const shouldRenderUsers = userIsAccountAdmin

  const displayDocsSubMenu = () => {
    hideThemeSubMenu()
    setShowDocsSubMenu(true)
  }

  const hideDocsSubMenu = () => {
    setShowDocsSubMenu(false)
  }

  const renderSettings = () => {
    return (
      <Link
        to="/settings"
        className="AccountPopup__menu-item AccountPopup__menu-item-link AccountPopup__settings"
        onMouseEnter={hideDocsSubMenu}
        onClick={props.closeAccountPopup}
      >
        <Settings
          className="AccountPopup__menu-item-icon"
        />
        Settings
      </Link>
    )
  }

  const displayThemeSubMenu = () => {
    hideDocsSubMenu()
    setShowThemeSubMenu(true)
  }

  const hideThemeSubMenu = () => {
    setShowThemeSubMenu(false)
  }

  const hideSubMenus = () => {
    hideDocsSubMenu()
    hideThemeSubMenu()
  }

  const renderUsers = () => {
    return (
      <Link
        to="/settings?settings=users"
        className="AccountPopup__menu-item AccountPopup__menu-item-link AccountPopup__users"
        onMouseEnter={hideDocsSubMenu}
        onClick={props.closeAccountPopup}
      >
        <Users
          className="AccountPopup__menu-item-icon"
        />
        Users
      </Link>
    )
  }

  const renderPlan = () => {
    return (
      <Link
        to="/subscriptions"
        className="AccountPopup__menu-item AccountPopup__menu-item-link AccountPopup__plan"
        onMouseEnter={hideSubMenus}
      >
        <Plan
          className="AccountPopup__menu-item-icon"
        />
        Plan
      </Link>
    )
  }

  const renderBilling = () => {
    return (
      <div
        className="AccountPopup__menu-item AccountPopup__billing"
        onClick={props.createCustomerPortalSession}
        onMouseEnter={hideSubMenus}
      >
        <Billing
          className="AccountPopup__menu-item-icon"
        />
        Billing
      </div>
    )
  }

  const renderStatistics = () => {
    return (
      <Link
        to="/settings?settings=statistics"
        className="AccountPopup__menu-item AccountPopup__menu-item-link AccountPopup__statistics"
        onMouseEnter={hideDocsSubMenu}
        onClick={props.closeAccountPopup}
      >
        <Statistics
          className="AccountPopup__menu-item-icon"
        />
        Statistics
      </Link>
    )
  }

  const renderDocsAndRessources = () => {
    return (
      <div
        className="AccountPopup__menu-item AccountPopup__docs AccountPopup__docs-submenu-element"
        onMouseEnter={displayDocsSubMenu}
      >
        <Docs
          className="AccountPopup__menu-item-icon"
        />
        Docs and ressources
        <Arrow
          className="AccountPopup__menu-item-right-icon"
        />
        {showDocsSubMenu && renderDocsSubMenu()}
      </div>
    )
  }

  const renderThemePreference = () => {
    return (
      <div
        className="AccountPopup__menu-item AccountPopup__theme AccountPopup__theme-submenu-element"
        onMouseEnter={displayThemeSubMenu}
      >
        <Theme
          className="AccountPopup__menu-item-icon"
        />
        Theme preference
        <Arrow
          className="AccountPopup__menu-item-right-icon"
        />
        {showThemeSubMenu && renderThemeSubMenu()}
      </div>
    )
  }

  const renderLogout = () => {
    return (
      <div
        className="AccountPopup__menu-item AccountPopup__logout"
        onClick={props.logout}
        onMouseEnter={hideSubMenus}
      >
        <Logout
          className="AccountPopup__menu-item-icon"
        />
        Log out
      </div>
    )
  }

  const renderDocsSubMenu = () => {
    return (
      <div className="AccountPopup__submenu AccountPopup__docs-submenu-element">
        <a
          className="AccountPopup__menu-item AccountPopup__menu-item-link"
          href="https://support.checksub.com"
          target="_blank"
        >
          <Documentation
            className="AccountPopup__menu-item-icon"
          />
          Documentation
          <ExternalLink
            className="AccountPopup__menu-item-right-icon"
          />
        </a>
        <a
          className="AccountPopup__menu-item AccountPopup__menu-item-link"
          href="https://support.checksub.com/changelog"
          target="_blank"
        >
          <Changelog
            className="AccountPopup__menu-item-icon"
          />
          Changelog
          <ExternalLink
            className="AccountPopup__menu-item-right-icon"
          />
        </a>
        <a
          className="AccountPopup__menu-item AccountPopup__menu-item-link"
          href="https://checksub.readme.io/reference"
          target="_blank"
        >
          <Api
            className="AccountPopup__menu-item-icon"
          />
          API Documentation
          <ExternalLink
            className="AccountPopup__menu-item-right-icon"
          />
        </a>
      </div>
    )
  }

  const renderThemeSubMenu = () => {
    return (
      <div className="AccountPopup__submenu AccountPopup__theme-submenu-element">
        <div
          className="AccountPopup__menu-item"
          onClick={props.handleResetUseLightTheme}
        >
          <DarkTheme
            className="AccountPopup__menu-item-icon"
          />
          Dark theme
        </div>
        <div
          className="AccountPopup__menu-item"
          onClick={props.handleSetUseLightTheme}
        >
          <LightTheme
            className="AccountPopup__menu-item-icon"
          />
          Light theme
        </div>
      </div>
    )
  }

  return (
    <div className="AccountPopup__menu">
      {renderSettings()}
      {shouldRenderUsers && renderUsers()}
      {shouldRenderBillingAndPlan && renderPlan()}
      {shouldRenderBillingAndPlan && renderBilling()}
      {renderStatistics()}
      {renderDocsAndRessources()}
      {renderThemePreference()}
      <div className="AccountPopup__line"/>
      {renderLogout()}
    </div>
  )
}

export default Menu
